import { createReducer, isAnyOf } from "@reduxjs/toolkit";
import {
  addPendingComment,
  createComment,
  createCommentFailure,
  createCommentSuccess,
  deleteComment,
  deleteCommentFailure,
  deleteCommentSuccess,
  editComment,
  editCommentFailure,
  editCommentSuccess,
  getComments,
  getCommentsFailure,
  getCommentsPortion,
  getCommentsPortionFailure,
  getCommentsPortionSuccess,
  getCommentsSuccess,
  likeComment,
  likeCommentFailure,
  likeCommentSuccess,
  resetEditCommentId,
  setEditCommentId,
  unlikeComment,
  unlikeCommentFailure,
  unlikeCommentSuccess,
} from "./actions";

const initialState = {
  allComments: [],
  commentById: {},
  pendingComments: [],
  cursor: undefined, // last post id
  totalCount: undefined,
  isLoading: true,
  error: null,
  editCommentId: undefined,
};

export default createReducer(initialState, (builder) => {
  builder
    .addCase(createComment.type, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(
      createCommentSuccess.type,
      (state, { payload: { comment, result } }) => ({
        ...state,
        allComments: [...state.allComments, result].sort(
          (first, second) => second - first,
        ),
        commentById: { ...state.commentById, [result]: comment },
        isLoading: false,
      }),
    )
    .addCase(createCommentFailure.type, (state, action) => ({
      ...state,
      isLoading: false,
      isCreated: false,
      error: action.payload,
    }))
    .addCase(getComments.type, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(getCommentsSuccess.type, (state, action) => ({
      ...state,
      ...action.payload,
      allComments: [...action.payload.allComments].sort(
        (first, second) => second - first,
      ),
      error: initialState.error,
      isLoading: false,
    }))
    .addCase(getCommentsFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isLoading: false,
    }))
    .addCase(getCommentsPortion.type, (state) => ({
      ...state,
      isLoading: true,
    }))
    .addCase(getCommentsPortionSuccess.type, (state, { payload }) => {
      const { allComments, commentById } = state;
      const {
        allComments: newComments,
        commentById: newCommentsById,
        totalCount,
        cursor,
      } = payload;

      return {
        ...state,
        cursor,
        allComments: [...new Set([...allComments, ...newComments])].sort(
          (first, second) => second - first,
        ),
        commentById: { ...commentById, ...newCommentsById },
        totalCount,
        error: initialState.error,
        isLoading: false,
      };
    })
    .addCase(getCommentsPortionFailure.type, (state, action) => ({
      ...state,
      error: action.payload,
      isLoading: false,
    }))
    .addCase(addPendingComment.type, (state, { payload }) => ({
      ...state,
      pendingComments: [...state.pendingComments, payload.commentId],
    }))
    .addCase(deleteComment.type, (state, { payload }) => {
      const { id } = payload;

      state.commentById[id].isDeleting = true;
    })
    .addCase(deleteCommentSuccess.type, (state, { payload }) => {
      const { id: commentId } = payload;

      state.commentById[commentId].isDeleting = false;
      state.allComments = state.allComments.filter((id) => id !== commentId);
    })
    .addCase(deleteCommentFailure.type, (state, { payload }) => {
      const { id } = payload;

      state.commentById[id].isDeleting = false;
    })
    .addCase(setEditCommentId.type, (state, { payload }) => {
      state.editCommentId = payload;
    })
    .addCase(resetEditCommentId.type, (state) => {
      state.editCommentId = initialState.editCommentId;
    })
    .addCase(editComment.type, (state, { payload }) => {
      const { id } = payload;

      state.commentById[id].isUpdating = true;
    })
    .addCase(editCommentSuccess.type, (state, { payload }) => {
      const { id } = payload;

      state.editCommentId = initialState.editCommentId;
      state.commentById[id] = payload;
    })
    .addCase(editCommentFailure.type, (state, { payload }) => {
      const { id } = payload;

      state.commentById[id].isUpdating = false;
    })
    .addMatcher(isAnyOf(likeComment, unlikeComment), (state, { payload }) => {
      const { id } = payload;

      state.commentById[id].isLikeStatusChanging = true;
    })
    .addMatcher(
      isAnyOf(likeCommentSuccess, unlikeCommentSuccess),
      (state, { payload }) => {
        const { id, likesCount } = payload;
        state.commentById[id].likesCount = likesCount;
        state.commentById[id].isLiked = !state.commentById[id].isLiked;
        state.commentById[id].isLikeStatusChanging = false;
      },
    )
    .addMatcher(
      isAnyOf(likeCommentFailure, unlikeCommentFailure),
      (state, { payload }) => {
        const { id } = payload;

        state.commentById[id].isLikeStatusChanging = false;
      },
    );
});
